import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PokemonCard, Deck, SocialProfile } from '../types';
import { geminiService } from '../services/geminiService';
import { socialService } from '../services/socialService';
import { pokemonService } from '../services/pokemonService';
import { Loader2, Zap, Users, Sparkles, X, Heart, Plus, TrendingUp, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Layers, Calendar, Hash, Brush, ExternalLink, Globe, Minus, Check, ShieldCheck, Star, Maximize2, Minimize2, Trash2 } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area } from 'recharts';

interface Props {
  card: PokemonCard;
  isOpen?: boolean;
  onClose: () => void;
  onToggleFavorite: (card: PokemonCard) => void;
  isFavorite: boolean;
  onToggleWishlist?: (card: PokemonCard) => void;
  isOnWishlist?: boolean;
  onUpdateQuantity?: (cardId: string, delta: number) => void;
  onUpdateCondition?: (cardId: string, condition: number) => void;
  decks: Deck[];
  onAddToDeck: (deckId: string, card: PokemonCard) => void;
  onCreateDeckAndAdd: (name: string, card: PokemonCard) => void;
  isAuthenticated: boolean;
  onNavigate?: (direction: 'next' | 'prev') => void;
  onSelectCard?: (card: PokemonCard) => void;
  onSelectSet?: (setName: string) => void;
  currentDeckId?: string | null;
  onRemoveFromDeck?: (deckId: string, cardId: string) => void;
}

interface CardAnalysis {
  competitiveTier: string;
  strengths: string[];
  weaknesses: string[];
  synergySuggestions: string[];
  deckBuildingTip: string;
  funFact: string;
}

const CardDetailsModal: React.FC<Props> = ({
  card,
  isOpen = true,
  onClose,
  onToggleFavorite,
  isFavorite,
  onToggleWishlist,
  isOnWishlist = false,
  onUpdateQuantity,
  onUpdateCondition,
  decks,
  onAddToDeck,
  onCreateDeckAndAdd,
  isAuthenticated,
  onNavigate,
  onSelectCard,
  onSelectSet,
  currentDeckId,
  onRemoveFromDeck
}) => {
  const [analysis, setAnalysis] = useState<CardAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [showDeckMenu, setShowDeckMenu] = useState(false);
  const [owners, setOwners] = useState<SocialProfile[]>([]);
  const [feedbackMsg, setFeedbackMsg] = useState<string | null>(null);
  const [relatedCards, setRelatedCards] = useState<PokemonCard[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);

  // Deck Creation State
  const [isCreatingDeck, setIsCreatingDeck] = useState(false);
  const [newDeckName, setNewDeckName] = useState('');

  // Mobile Specific State
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [showMobileDeckMenu, setShowMobileDeckMenu] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Swipe State
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const touchEndY = useRef<number | null>(null);

  const showFeedback = (msg: string) => {
    setFeedbackMsg(msg);
    setTimeout(() => setFeedbackMsg(null), 2000);
  };

  const fetchAnalysis = useCallback(async () => {
    setLoading(true);
    setAnalysis(null);
    try {
      const result = await geminiService.analyzeCard(card);
      setAnalysis(result);
    } catch (error) {
      console.error("AI Analysis failed", error);
    } finally {
      setLoading(false);
    }
  }, [card]);

  const fetchOwners = useCallback(async () => {
    try {
      const foundOwners = await socialService.getOwnersOfCard(card.id);
      setOwners(foundOwners);
    } catch (e) {
      console.warn("Failed to fetch owners", e);
    }
  }, [card.id]);

  const fetchRelatedCards = useCallback(async () => {
    setLoadingRelated(true);
    try {
      // Search for other cards with the same name
      const result = await pokemonService.searchCards(`name:"${card.name}"`);
      // Keep up to 12 cards, including the current one for stability
      const sorted = result.data.sort((a, b) => {
        // Sort by set name for stable ordering
        return a.set.name.localeCompare(b.set.name);
      }).slice(0, 12);

      setRelatedCards(sorted);
    } catch (e) {
      console.warn("Failed to fetch related cards", e);
    } finally {
      setLoadingRelated(false);
    }
  }, [card.name]);

  useEffect(() => {
    if (isOpen) {
      fetchAnalysis();
      fetchOwners();
      fetchRelatedCards();
    }
  }, [isOpen, fetchAnalysis, fetchOwners, fetchRelatedCards]);

  // Keyboard Navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        onNavigate?.('prev');
      } else if (e.key === 'ArrowRight') {
        onNavigate?.('next');
      } else if (e.key === 'Escape') {
        if (isFullScreen) setIsFullScreen(false);
        else onClose();
      } else if (e.key === 'ArrowUp') {
        setIsDetailsOpen(true);
      } else if (e.key === 'ArrowDown') {
        setIsDetailsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onNavigate, onClose, isFullScreen]);

  // Touch Swipe Handlers (4-Way Navigation)
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
    touchStartY.current = e.targetTouches[0].clientY;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
    touchEndY.current = e.targetTouches[0].clientY;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current || !touchStartY.current || !touchEndY.current) return;

    const xDiff = touchStartX.current - touchEndX.current;
    const yDiff = touchStartY.current - touchEndY.current;

    // SWIPE SENSITIVITY FIX (Cushion)
    const SWIPE_THRESHOLD = 50; // Increased sensitivity
    const AXIS_LOCK_RATIO = 1.5;

    // Determine prominent axis
    if (Math.abs(xDiff) > Math.abs(yDiff) * AXIS_LOCK_RATIO) {
      // Horizontal Swipe (Navigate Cards)
      if (Math.abs(xDiff) > SWIPE_THRESHOLD) {
        onNavigate?.(xDiff > 0 ? 'next' : 'prev');
      }
    } else if (Math.abs(yDiff) > Math.abs(xDiff) * AXIS_LOCK_RATIO) {
      // Vertical Swipe (Toggle Details)
      // Only if NOT full screen
      if (!isFullScreen) {
        if (yDiff > SWIPE_THRESHOLD) { // Swipe UP
          setIsDetailsOpen(true);
        } else if (yDiff < -SWIPE_THRESHOLD) { // Swipe DOWN
          if (isDetailsOpen) setIsDetailsOpen(false);
          else onClose(); // Close modal if details already closed
        }
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
    touchStartY.current = null;
    touchEndY.current = null;
  };

  const handleCreateDeck = (isMobile: boolean = false) => {
    if (!newDeckName.trim()) return;
    onCreateDeckAndAdd(newDeckName, card);
    showFeedback(`Formation "${newDeckName}" Created`);
    setNewDeckName('');
    setIsCreatingDeck(false);
    setShowDeckMenu(false);
    if (isMobile) setShowMobileDeckMenu(false);
  };

  const handleAddToDeck = (deckId: string, deckName: string, isMobile: boolean = false) => {
    onAddToDeck(deckId, card);
    showFeedback(`Added to ${deckName}`);
    setShowDeckMenu(false);
    if (isMobile) setShowMobileDeckMenu(false);
  };

  const getPriceData = () => {
    const prices = card.tcgplayer?.prices;
    if (!prices) return null;
    const priceType = Object.keys(prices)[0];
    const data = prices[priceType];
    return {
      type: priceType,
      low: data.low,
      mid: data.mid,
      high: data.high,
      market: data.market,
      updatedAt: card.tcgplayer?.updatedAt
    };
  };

  const marketData = getPriceData();
  const priceHistory = Array.from({ length: 7 }, (_, i) => ({
    day: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
    price: (marketData?.market || 50) * (0.95 + Math.random() * 0.1)
  }));

  // Condition Helper
  const currentCondition = card.condition || 9;
  const getConditionLabel = (c: number) => {
    if (c >= 10) return "Gem Mint";
    if (c >= 9) return "Near Mint";
    if (c >= 7) return "Excellent";
    if (c >= 5) return "Played";
    if (c >= 3) return "Heavy Play";
    return "Damaged";
  };
  const getConditionColor = (c: number) => {
    if (c >= 9) return "text-green-400";
    if (c >= 7) return "text-blue-400";
    if (c >= 5) return "text-yellow-400";
    return "text-red-400";
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-950/95 backdrop-blur-3xl overflow-hidden">

      {/* Toast Feedback */}
      {feedbackMsg && (
        <div className="fixed top-10 left-1/2 -translate-x-1/2 z-[200] bg-green-500/90 backdrop-blur-md px-6 py-3 rounded-full shadow-[0_0_20px_rgba(34,197,94,0.4)] flex items-center gap-3 animate-in slide-in-from-top-4 fade-in duration-300">
          <Check className="w-4 h-4 text-white" />
          <span className="text-white text-xs font-black uppercase tracking-widest">{feedbackMsg}</span>
        </div>
      )}

      {/* Desktop Navigation Arrows */}
      <button
        onClick={(e) => { e.stopPropagation(); onNavigate?.('prev'); }}
        className="fixed left-4 top-1/2 -translate-y-1/2 z-[60] p-4 bg-slate-800/30 hover:bg-slate-700/80 rounded-full backdrop-blur-sm transition-all hidden md:flex items-center justify-center group border border-white/5 active:scale-95"
        aria-label="Previous card"
      >
        <ChevronLeft className="w-8 h-8 text-white opacity-50 group-hover:opacity-100" />
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); onNavigate?.('next'); }}
        className="fixed right-4 top-1/2 -translate-y-1/2 z-[60] p-4 bg-slate-800/30 hover:bg-slate-700/80 rounded-full backdrop-blur-sm transition-all hidden md:flex items-center justify-center group border border-white/5 active:scale-95"
        aria-label="Next card"
      >
        <ChevronRight className="w-8 h-8 text-white opacity-50 group-hover:opacity-100" />
      </button>

      {/* Close Button (Hidden in Mobile Full Screen) */}
      {!isFullScreen && (
        <button
          onClick={onClose}
          className="fixed top-6 right-6 z-[100] p-3 rounded-full bg-slate-900/50 hover:bg-slate-700 transition-all shadow-2xl backdrop-blur-md group active:scale-90 border border-white/10"
          aria-label="Close details"
        >
          <X className="w-6 h-6 text-slate-300 group-hover:rotate-90 transition-transform duration-300" />
        </button>
      )}

      <div
        className={`relative w-full h-full md:max-w-7xl md:h-[90vh] md:bg-[#0a0f1e] md:rounded-[3rem] md:shadow-[0_0_150px_rgba(0,0,0,1)] md:border md:border-slate-800/50 flex flex-col md:flex-row overflow-hidden ${isFullScreen ? 'z-[200] bg-black' : ''}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >

        {/* === LEFT COLUMN / MOBILE BACKGROUND (Image & Actions) === */}
        {/* Updated Layout: Flex Column with flex-1 on image to allow shrinking, ensuring buttons are always visible */}
        <div className={`md:w-[450px] w-full h-full bg-slate-950 md:p-8 flex flex-col border-r border-slate-800/50 relative transition-all duration-500 ${isDetailsOpen && !isFullScreen ? 'blur-sm scale-95 opacity-50 md:blur-0 md:scale-100 md:opacity-100' : 'blur-0 scale-100 opacity-100'} ${isFullScreen ? 'bg-black border-none' : ''}`}>

          {/* Card Image Container - Flex Grow/Shrink */}
          <div className={`relative z-10 flex-1 min-h-0 flex items-center justify-center w-full group ${isFullScreen ? 'p-0' : 'p-4 md:p-0'}`}>
            {!isFullScreen && <div className="absolute inset-0 bg-blue-600 rounded-[2rem] blur-[60px] opacity-20 group-hover:opacity-30 transition-opacity duration-700 hidden md:block pointer-events-none"></div>}
            <img
              src={isFullScreen ? (card.images.large || card.images.small) : card.images.large}
              alt={card.name}
              className={`relative max-h-full w-auto object-contain drop-shadow-2xl transition-all duration-700 ${isFullScreen ? 'scale-100' : 'md:group-hover:scale-105 md:group-hover:-rotate-1'}`}
              onClick={() => setIsFullScreen(!isFullScreen)} // Toggle on tap
            />

            {/* Vault Status (Mobile Asset Badge) */}
            {isFavorite && !isFullScreen && (
              <div className="absolute top-8 left-8 md:hidden z-20 animate-in zoom-in slide-in-from-top-4 duration-500">
                <div className="bg-pink-600/90 backdrop-blur-md border border-pink-400/50 px-4 py-2 rounded-2xl shadow-[0_0_20px_rgba(219,39,119,0.4)] flex flex-col items-center">
                  <span className="text-[8px] font-black text-white/80 uppercase tracking-widest leading-none mb-1">In Vault</span>
                  <div className="flex items-center gap-1.5">
                    <Heart className="w-3 h-3 text-white fill-current" />
                    <span className="text-lg font-black text-white font-mono leading-none">{card.quantity || 1}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Full Screen Toggle Button (Mobile Overlay) */}
            <button
              onClick={(e) => { e.stopPropagation(); setIsFullScreen(!isFullScreen); }}
              className="absolute top-4 right-4 md:hidden p-2 bg-black/50 backdrop-blur-md rounded-full text-white border border-white/10 z-50"
            >
              {isFullScreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>

          {/* Mobile: TAP Up Hint - RAISED HIGHER */}
          {!isDetailsOpen && !isFullScreen && (
            <button
              onClick={() => setIsDetailsOpen(true)}
              className="absolute bottom-24 left-0 right-0 h-24 bg-gradient-to-t from-black/80 to-transparent flex flex-col items-center justify-end pb-6 animate-pulse md:hidden z-30 pointer-events-none"
            >
              <span className="text-[10px] font-black uppercase text-white tracking-[0.3em] mb-2 drop-shadow-lg">Tap for Intel</span>
              <ChevronUp className="w-6 h-6 text-white drop-shadow-lg" />
            </button>
          )}

          {/* Desktop: Action Buttons - Fixed at bottom, won't be pushed off screen */}
          <div className="hidden md:flex flex-col gap-3 w-full mt-6 flex-shrink-0 z-20 relative">
            {/* Vault + Wishlist Row */}
            <div className="flex gap-3">
              <button
                onClick={() => { onToggleFavorite(card); showFeedback(isFavorite ? 'Removed from Vault' : 'Secured in Vault'); }}
                className={`flex-1 flex items-center justify-center gap-3 py-4 rounded-[1.2rem] font-black uppercase tracking-[0.2em] text-xs transition-all border ${isFavorite ? 'bg-pink-600 text-white border-pink-400 shadow-xl shadow-pink-600/20' : 'bg-slate-900 text-slate-400 hover:bg-slate-800 border-white/10 hover:border-white/20'}`}
              >
                <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current animate-heartbeat' : ''}`} />
                {isFavorite ? 'SAVED' : 'VAULT'}
              </button>

              {onToggleWishlist && (
                <button
                  onClick={() => { onToggleWishlist(card); showFeedback(isOnWishlist ? 'Removed from Wishlist' : 'Added to Wishlist'); }}
                  className={`flex items-center justify-center gap-2 px-5 py-4 rounded-[1.2rem] font-black uppercase tracking-[0.15em] text-xs transition-all border ${isOnWishlist ? 'bg-yellow-500 text-black border-yellow-300 shadow-xl shadow-yellow-500/20' : 'bg-slate-900 text-slate-400 hover:bg-slate-800 border-white/10 hover:border-white/20'}`}
                  title={isOnWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}
                >
                  <Star className={`w-5 h-5 ${isOnWishlist ? 'fill-current' : ''}`} />
                  {isOnWishlist ? 'WANTED' : 'WANT'}
                </button>
              )}
            </div>

            {/* Quantity Controls (Only if Favorite) */}
            {isFavorite && onUpdateQuantity && (
              <div className="flex items-center justify-between bg-slate-900 border border-slate-800 rounded-[1.2rem] p-2 animate-in slide-in-from-bottom-2 fade-in">
                <button
                  onClick={() => onUpdateQuantity(card.id, -1)}
                  className="p-3 hover:bg-slate-800 rounded-xl transition-colors text-slate-400 hover:text-white"
                  disabled={(card.quantity || 1) <= 1}
                  aria-label="Decrease quantity"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <div className="flex flex-col items-center">
                  <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Units</span>
                  <span className="text-lg font-black text-white font-mono">{card.quantity || 1}</span>
                </div>
                <button
                  onClick={() => onUpdateQuantity(card.id, 1)}
                  className="p-3 hover:bg-slate-800 rounded-xl transition-colors text-slate-400 hover:text-white"
                  aria-label="Increase quantity"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            )}

            <button
              onClick={() => isAuthenticated ? setShowDeckMenu(!showDeckMenu) : alert('Biometric Auth Required')}
              className="w-full flex items-center justify-center gap-3 py-4 rounded-[1.2rem] bg-blue-600 text-white font-black uppercase tracking-[0.2em] text-xs hover:bg-blue-500 transition-all shadow-xl shadow-blue-600/30 border border-white/20 hover:border-white/40"
            >
              <Plus className="w-5 h-5" />
              DEPLOY TO FORMATION
            </button>

            {currentDeckId && onRemoveFromDeck && (
              <button
                onClick={() => onRemoveFromDeck(currentDeckId, card.id)}
                className="w-full flex items-center justify-center gap-3 py-4 rounded-[1.2rem] bg-red-600/10 text-red-500 font-black uppercase tracking-[0.2em] text-xs hover:bg-red-600 hover:text-white transition-all border border-red-500/20"
              >
                <Trash2 className="w-5 h-5" />
                PURGE FROM FORMATION
              </button>
            )}

            {/* Dropdown Menu */}
            {showDeckMenu && (
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 absolute bottom-full mb-2 left-0 right-0 z-50 shadow-2xl">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-xs text-center text-slate-500 uppercase font-bold tracking-widest">Select Formation</p>
                  <button onClick={() => setShowDeckMenu(false)} aria-label="Close formation menu"><X className="w-4 h-4 text-slate-600 hover:text-white" /></button>
                </div>

                <div className="flex flex-col gap-2 max-h-48 overflow-y-auto custom-scrollbar">
                  {/* Deck Creation Input */}
                  {isCreatingDeck ? (
                    <div className="flex items-center gap-2 p-1">
                      <input
                        autoFocus
                        type="text"
                        placeholder="Formation Name..."
                        className="flex-1 bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-blue-500"
                        value={newDeckName}
                        onChange={(e) => setNewDeckName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleCreateDeck(false)}
                        aria-label="New formation name"
                      />
                      <button onClick={() => handleCreateDeck(false)} className="p-2 bg-blue-600 rounded-lg text-white hover:bg-blue-500" aria-label="Confirm create formation"><Check className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsCreatingDeck(true)}
                      className="p-3 bg-blue-600/10 hover:bg-blue-600/20 border border-blue-600/30 rounded-lg text-xs font-bold text-blue-400 text-left flex items-center gap-2 transition-colors"
                      aria-label="Create new formation"
                    >
                      <Plus className="w-3 h-3" /> Create New Formation
                    </button>
                  )}

                  {/* Existing Decks */}
                  {decks.map(d => (
                    <button
                      key={d.id}
                      onClick={() => handleAddToDeck(d.id, d.name, false)}
                      className="p-3 bg-slate-800 hover:bg-slate-700 rounded-lg text-xs font-bold text-slate-300 text-left flex justify-between items-center group"
                      aria-label={`Add to formation ${d.name}`}
                    >
                      {d.name}
                      <span className="text-[9px] bg-slate-900 px-1.5 py-0.5 rounded text-slate-500 group-hover:text-white">{d.cards.length}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* === RIGHT COLUMN / MOBILE DRAWER (Intel) === */}
        {!isFullScreen && (
          <div
            className={`
              absolute md:relative bottom-0 left-0 right-0
              bg-[#080c1a]/95 backdrop-blur-xl md:bg-[#080c1a]
              rounded-t-[2.5rem] md:rounded-none
              transition-transform duration-500 cubic-bezier(0.32, 0.72, 0, 1)
              flex-1 flex flex-col
              z-40 border-t border-slate-700/50 md:border-t-0
              ${isDetailsOpen ? 'translate-y-0 h-[85vh]' : 'translate-y-full md:translate-y-0 h-0 md:h-auto'}
            `}
          >
            {/* Mobile Drawer Handle */}
            <div
              className="md:hidden w-full flex flex-col items-center justify-center py-4 cursor-pointer active:opacity-50"
              onClick={() => setIsDetailsOpen(false)}
              aria-label="Close details drawer"
            >
              <ChevronDown className="w-8 h-8 text-slate-500 animate-bounce" />
            </div>
            <div className="md:hidden text-center pb-2 text-[10px] text-slate-500 font-bold uppercase tracking-widest">Swipe Down to Close</div>

            <div className="flex-1 p-6 md:p-12 overflow-y-auto custom-scrollbar">
              <div className="flex flex-col gap-12 max-w-4xl mx-auto pb-32 md:pb-0">

                {/* Header Info */}
                <header>
                  <div className="flex items-center flex-wrap gap-4 mb-5">
                    <h2 className="text-4xl md:text-6xl font-black font-heading text-white tracking-tighter uppercase leading-[0.8] break-words max-w-full">{card.name}</h2>
                    <div className="flex gap-2">
                      <span className="px-5 py-2 rounded-2xl bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[10px] font-black uppercase tracking-[0.2em]">
                        {card.supertype}
                      </span>
                      <span className="px-5 py-2 rounded-2xl bg-slate-800/50 text-slate-400 border border-slate-700 text-[10px] font-black uppercase tracking-[0.2em]">
                        {card.rarity || 'Common'}
                      </span>
                    </div>
                  </div>

                  {/* Tactical Meta Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <button
                      onClick={() => onSelectSet?.(card.set.name)}
                      className="bg-slate-900/50 p-4 rounded-2xl border border-slate-800 flex flex-col gap-1 text-left hover:bg-slate-800 hover:border-blue-500/50 transition-all group/set"
                    >
                      <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1 group-hover/set:text-blue-400"><Layers className="w-3 h-3" /> Set Origin</span>
                      <span className="text-xs font-bold text-white truncate group-hover/set:text-blue-300">{card.set.name}</span>
                    </button>
                    <div className="bg-slate-900/50 p-4 rounded-2xl border border-slate-800 flex flex-col gap-1">
                      <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><Calendar className="w-3 h-3" /> Released</span>
                      <span className="text-xs font-bold text-white">{card.set.releaseDate ? new Date(card.set.releaseDate).getFullYear() : 'Unknown'}</span>
                    </div>
                    <div className="bg-slate-900/50 p-4 rounded-2xl border border-slate-800 flex flex-col gap-1">
                      <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><Brush className="w-3 h-3" /> Artist</span>
                      <span className="text-xs font-bold text-white truncate">{card.artist || 'N/A'}</span>
                    </div>
                    <div className="bg-slate-900/50 p-4 rounded-2xl border border-slate-800 flex flex-col gap-1">
                      <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><Hash className="w-3 h-3" /> Serial ID</span>
                      <span className="text-xs font-bold text-white font-mono">{card.number}/{card.set.printedTotal}</span>
                    </div>
                  </div>

                  <p className="text-slate-500 italic text-sm md:text-lg leading-relaxed font-medium break-words">"{card.flavorText || "The data archives contain no historical flavor text for this tactical entity."}"</p>
                </header>

                {/* Mobile Only: Action Buttons inside Drawer */}
                <div className="md:hidden">
                  {!showMobileDeckMenu ? (
                    <div className="space-y-4">
                      <div className="flex flex-col gap-4">
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            onClick={() => { onToggleFavorite(card); showFeedback(isFavorite ? 'Removed from Vault' : 'Secured in Vault'); }}
                            className={`flex flex-col items-center justify-center gap-2 py-5 rounded-[2rem] font-black uppercase tracking-widest text-[10px] border transition-all active:scale-95 ${isFavorite ? 'bg-pink-600 border-pink-500 text-white shadow-[0_0_30px_rgba(219,39,119,0.3)]' : 'bg-slate-900/50 border-slate-800 text-slate-400'}`}
                            aria-label={isFavorite ? 'Remove from Vault' : 'Add to Vault'}
                          >
                            <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current animate-heartbeat' : ''}`} />
                            {isFavorite ? 'Saved' : 'Save'}
                          </button>
                          {onToggleWishlist && (
                            <button
                              onClick={() => { onToggleWishlist(card); showFeedback(isOnWishlist ? 'Removed from Wishlist' : 'Added to Wishlist'); }}
                              className={`flex flex-col items-center justify-center gap-2 py-5 rounded-[2rem] font-black uppercase tracking-widest text-[10px] border transition-all active:scale-95 ${isOnWishlist ? 'bg-yellow-500 border-yellow-400 text-black shadow-[0_0_30px_rgba(234,179,8,0.3)]' : 'bg-slate-900/50 border-slate-800 text-slate-400'}`}
                              aria-label={isOnWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}
                            >
                              <Star className={`w-5 h-5 ${isOnWishlist ? 'fill-current' : ''}`} />
                              {isOnWishlist ? 'Wanted' : 'Want'}
                            </button>
                          )}
                        </div>

                        {/* Mobile Quantity Stepper */}
                        {isFavorite && onUpdateQuantity && (
                          <div className="flex items-center justify-between bg-slate-900/80 border border-slate-800 rounded-[2rem] p-2 animate-in slide-in-from-bottom-4 duration-500">
                            <button
                              onClick={() => onUpdateQuantity(card.id, -1)}
                              className="w-14 h-14 flex items-center justify-center bg-slate-800 hover:bg-slate-700 rounded-2xl text-white active:scale-90 transition-all"
                              disabled={(card.quantity || 1) <= 1}
                              aria-label="Decrease quantity"
                            >
                              <Minus className="w-5 h-5" />
                            </button>
                            <div className="flex flex-col items-center">
                              <span className="text-[9px] font-black text-slate-500 uppercase tracking-[0.2em] mb-1">In Stock</span>
                              <span className="text-2xl font-black text-white font-mono">{card.quantity || 1}</span>
                            </div>
                            <button
                              onClick={() => onUpdateQuantity(card.id, 1)}
                              className="w-14 h-14 flex items-center justify-center bg-blue-600 hover:bg-blue-500 rounded-2xl text-white active:scale-90 transition-all shadow-lg shadow-blue-600/20"
                              aria-label="Increase quantity"
                            >
                              <Plus className="w-5 h-5" />
                            </button>
                          </div>
                        )}

                        <button
                          onClick={() => setShowMobileDeckMenu(true)}
                          className="w-full flex items-center justify-center gap-3 py-5 rounded-[2.5rem] bg-blue-600 border border-blue-500 text-white font-black uppercase tracking-[0.2em] text-[11px] shadow-xl shadow-blue-600/30 active:scale-[0.98] transition-all"
                          aria-label="Deploy to formation"
                        >
                          <Layers className="w-5 h-5" />
                          Add to Formation
                        </button>
                      </div>
                    </div>
                  ) : (
                    // MOBILE DECK SUBMENU
                    <div className="bg-slate-900 rounded-2xl border border-slate-800 p-4 animate-in slide-in-from-right fade-in">
                      <div className="flex justify-between items-center mb-4 pb-2 border-b border-slate-800">
                        <h4 className="text-xs font-black uppercase text-white tracking-widest">Manage Formations</h4>
                        <button onClick={() => setShowMobileDeckMenu(false)} className="text-slate-500 hover:text-white" aria-label="Close formation menu"><X className="w-4 h-4" /></button>
                      </div>

                      {/* New Deck Input for Mobile */}
                      <div className="flex gap-2 mb-4">
                        <input
                          type="text"
                          placeholder="New Deck Name..."
                          className="flex-1 bg-slate-950 border border-slate-700 rounded-lg px-3 py-3 text-sm text-white focus:outline-none focus:border-blue-500"
                          value={newDeckName}
                          onChange={(e) => setNewDeckName(e.target.value)}
                          aria-label="New formation name"
                        />
                        <button onClick={() => handleCreateDeck(true)} className="bg-blue-600 text-white px-4 rounded-lg font-bold" aria-label="Confirm create formation"><Plus className="w-5 h-5" /></button>
                      </div>

                      {/* Deck List */}
                      <div className="flex flex-col gap-2 max-h-40 overflow-y-auto custom-scrollbar">
                        {decks.length > 0 ? decks.map(d => (
                          <button
                            key={d.id}
                            onClick={() => handleAddToDeck(d.id, d.name, true)}
                            className="p-3 bg-slate-950 hover:bg-slate-800 rounded-lg text-xs font-bold text-slate-300 text-left flex justify-between items-center active:bg-slate-800"
                            aria-label={`Add to formation ${d.name}`}
                          >
                            {d.name}
                            <span className="text-[9px] bg-slate-800 px-2 py-0.5 rounded text-slate-500">{d.cards.length}</span>
                          </button>
                        )) : (
                          <p className="text-center text-[10px] text-slate-600 py-2">No formations initialized.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* RELATED DEPLOYMENTS (SIMILAR CARDS) */}
                {(relatedCards.length > 0 || loadingRelated) && (
                  <div className="bg-slate-900/50 border border-slate-800 rounded-[2rem] p-6 mb-8 relative overflow-hidden">
                    <h3 className="text-[10px] font-black text-slate-500 uppercase flex items-center gap-2 tracking-[0.2em] mb-4">
                      <Layers className="w-4 h-4 text-purple-500" /> Similar Deployments
                    </h3>

                    <div className="flex space-x-4 overflow-x-auto pb-4 custom-scrollbar">
                      {loadingRelated && relatedCards.length === 0 ? (
                        // Initial Skeleton
                        Array.from({ length: 6 }).map((_, i) => (
                          <div key={i} className="w-24 h-32 bg-slate-800/50 rounded-xl animate-pulse flex-shrink-0" />
                        ))
                      ) : (
                        relatedCards.map(related => {
                          const isActive = related.id === card.id;
                          return (
                            <div
                              key={related.id}
                              className={`relative group cursor-pointer flex-shrink-0 w-24 transition-all duration-300 ${isActive ? 'scale-105 z-10' : 'opacity-60 hover:opacity-100'}`}
                              onClick={() => {
                                if (isActive) return;
                                setIsFullScreen(false);
                                onSelectCard?.(related);
                              }}
                              aria-label={`View details for ${related.name}`}
                            >
                              <div className={`absolute -inset-1 bg-gradient-to-t from-purple-600 to-indigo-600 rounded-2xl blur opacity-0 transition-opacity duration-300 ${isActive ? 'opacity-40' : 'group-hover:opacity-20'}`}></div>
                              <img
                                src={related.images.small}
                                alt={related.name}
                                className={`w-full rounded-xl shadow-lg relative ${isActive ? 'ring-2 ring-purple-500/50 border border-purple-400' : 'border border-transparent'}`}
                              />
                              <div className={`mt-2 text-[9px] font-black uppercase text-center truncate ${isActive ? 'text-purple-400' : 'text-slate-500'}`}>
                                {related.set.name}
                              </div>
                              {isActive && (
                                <div className="absolute -top-2 -right-2 bg-purple-500 text-[8px] font-black text-white px-2 py-0.5 rounded-full shadow-lg">
                                  VIEWING
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Subtle update indicator */}
                    {loadingRelated && relatedCards.length > 0 && (
                      <div className="absolute top-6 right-6 flex items-center gap-2">
                        <Loader2 className="w-3 h-3 text-purple-500 animate-spin" />
                        <span className="text-[8px] font-black text-purple-500 uppercase tracking-widest">Updating...</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Known Owners / Social Ledger */}
                {owners.length > 0 && (
                  <div className="bg-slate-900/50 border border-slate-800 rounded-[2rem] p-6 mb-8">
                    <h3 className="text-[10px] font-black text-slate-500 uppercase flex items-center gap-2 tracking-[0.2em] mb-4">
                      <Globe className="w-4 h-4 text-blue-500" /> Confirmed Asset Holders
                    </h3>
                    <div className="flex -space-x-3 overflow-x-auto pb-2">
                      {owners.map(owner => (
                        <div key={owner.id} className="relative group cursor-pointer flex-shrink-0" title={owner.name}>
                          <img
                            src={owner.avatar}
                            alt={owner.name}
                            className="w-10 h-10 rounded-full object-cover border-2 border-slate-900 transition-transform hover:scale-110 hover:z-10"
                          />
                          {owner.isOnline && <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-slate-900 rounded-full"></div>}
                        </div>
                      ))}
                      <div className="w-10 h-10 rounded-full bg-slate-800 border-2 border-slate-900 flex items-center justify-center text-[10px] font-bold text-slate-400">
                        +{owners.length}
                      </div>
                    </div>
                  </div>
                )}

                {/* Asset Condition Assessment (VAULT ONLY) */}
                {isFavorite && onUpdateCondition && (
                  <div className="bg-slate-900/80 rounded-[2rem] p-6 md:p-8 border border-slate-800 shadow-xl mb-8 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-6 opacity-5 pointer-events-none">
                      <ShieldCheck className="w-32 h-32 text-blue-400" />
                    </div>
                    <h3 className="text-[10px] font-black text-slate-500 uppercase flex items-center gap-3 tracking-[0.3em] mb-6">
                      <ShieldCheck className="w-5 h-5 text-blue-500" /> Asset Condition Grading
                    </h3>

                    <div className="flex flex-col md:flex-row md:items-center gap-8">
                      {/* Rating Display */}
                      <div className="flex flex-col items-center justify-center bg-slate-950 p-6 rounded-2xl border border-slate-800 w-full md:w-auto min-w-[140px]">
                        <span className={`text-4xl font-black ${getConditionColor(currentCondition)}`}>{currentCondition.toFixed(1)}</span>
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">{getConditionLabel(currentCondition)}</span>
                      </div>

                      {/* Controls */}
                      <div className="flex-1 w-full">
                        <input
                          type="range"
                          min="1"
                          max="10"
                          step="0.5"
                          value={currentCondition}
                          onChange={(e) => onUpdateCondition(card.id, parseFloat(e.target.value))}
                          className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-blue-500 mb-4"
                          aria-label="Condition slider"
                        />
                        <div className="flex justify-between text-[9px] font-bold text-slate-600 uppercase tracking-widest">
                          <span>Damaged (1)</span>
                          <span>Played (5)</span>
                          <span>Near Mint (9)</span>
                          <span>Gem Mint (10)</span>
                        </div>
                        <p className="mt-4 text-xs text-slate-400 leading-relaxed max-w-lg">
                          *Valuation adjusted automatically based on condition. Gem Mint (10) assets command a 50% premium over market price.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Price Data */}
                <div className="bg-[#0c1226] rounded-[2rem] p-6 md:p-8 border border-slate-800 shadow-inner">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-[10px] font-black text-slate-500 uppercase flex items-center gap-3 tracking-[0.3em]">
                      <TrendingUp className="w-5 h-5 text-green-500" /> MARKET PULSE
                    </h3>
                    <div className="flex items-center gap-2 px-3 py-1 bg-green-500/10 rounded-full border border-green-500/20">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-[8px] font-black text-green-500 uppercase">LIVE</span>
                    </div>
                  </div>

                  <div className="flex items-baseline gap-4 mb-6 flex-wrap">
                    <span className="text-5xl font-black text-white font-mono tracking-tighter">
                      ${marketData?.market?.toFixed(2) || 'N/A'}
                    </span>
                    {card.tcgplayer?.url && (
                      <a
                        href={card.tcgplayer.url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-xl border border-blue-500/20 transition-all group"
                      >
                        <span className="text-[10px] font-black uppercase tracking-widest">TCG Player Market</span>
                        <ExternalLink className="w-3 h-3 group-hover:scale-110 transition-transform" />
                      </a>
                    )}
                  </div>

                  <div className="h-24 w-full -mx-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={priceHistory}>
                        <defs>
                          <linearGradient id="colorPriceMobile" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <Area type="monotone" dataKey="price" stroke="#22c55e" fillOpacity={1} fill="url(#colorPriceMobile)" strokeWidth={3} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* AI Strategic Matrix */}
                <div className="bg-gradient-to-br from-[#1a233b] to-[#0f172a] rounded-[3rem] p-6 md:p-10 border border-slate-700/30 relative overflow-hidden shadow-2xl">
                  <div className="absolute top-0 right-0 p-8 opacity-10">
                    <Sparkles className="w-40 h-40 text-yellow-400" />
                  </div>

                  <h3 className="text-xl md:text-2xl font-black text-yellow-400 mb-10 flex items-center gap-4 tracking-tighter">
                    <div className="p-3 bg-yellow-400/10 rounded-2xl border border-yellow-400/20">
                      <Sparkles className="w-6 h-6 md:w-8 md:h-8" />
                    </div>
                    GEMINI TACTICAL SYNTHESIS
                  </h3>

                  {loading ? (
                    <div className="flex flex-col items-center justify-center py-12 md:py-24">
                      <Loader2 className="w-12 h-12 md:w-16 md:h-16 text-yellow-400 animate-spin mb-4" />
                      <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.5em] animate-pulse">Computing...</p>
                    </div>
                  ) : analysis ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                      <div className="space-y-6">
                        <div className="bg-red-600 px-6 py-4 rounded-3xl shadow-2xl">
                          <span className="text-3xl md:text-4xl font-black text-white font-heading block">{analysis.competitiveTier}</span>
                          <span className="text-[10px] font-black text-white/90 uppercase tracking-widest">TACTICAL TIER</span>
                        </div>
                        <div>
                          <h4 className="flex items-center gap-2 text-xs font-black text-green-400 mb-3 uppercase tracking-[0.2em]"><Zap className="w-4 h-4" /> Strengths</h4>
                          <div className="space-y-2">
                            {analysis.strengths.map((s: string, i: number) => (
                              <div key={i} className="bg-slate-950/60 p-4 rounded-xl border border-slate-800 text-xs text-slate-300 font-semibold break-words">{s}</div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div>
                        <h4 className="flex items-center gap-2 text-xs font-black text-blue-400 mb-3 uppercase tracking-[0.2em]"><Users className="w-4 h-4" /> Synergy</h4>
                        <div className="space-y-2">
                          {analysis.synergySuggestions.map((s: string, i: number) => (
                            <div key={i} className="bg-slate-950/60 p-4 rounded-xl border border-slate-800 text-xs text-slate-300 font-semibold break-words">{s}</div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-xs font-bold text-slate-600 uppercase">Uplink Offline</div>
                  )}
                </div>

                {/* Moves & Attacks */}
                <div className="bg-[#0c1226] p-6 md:p-10 rounded-[2.5rem] border border-slate-800 shadow-xl">
                  <h4 className="text-[10px] font-black text-slate-500 uppercase mb-6 tracking-[0.3em]">OPERATIONAL MOVES</h4>
                  <div className="space-y-6">
                    {card.attacks?.map((attack, i) => (
                      <div key={i} className="pb-6 border-b border-slate-900 last:border-0">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm md:text-md font-black text-slate-100 uppercase tracking-tight">{attack.name}</span>
                          <span className="text-xl font-black text-red-500 font-heading">{attack.damage || ''}</span>
                        </div>
                        <div className="flex gap-1 mb-2">
                          {attack.cost.map((c, ci) => (
                            <div key={ci} className="w-3 h-3 rounded-full bg-slate-800 border border-slate-700 shadow-inner" />
                          ))}
                        </div>
                        <p className="text-xs text-slate-500 leading-relaxed font-medium break-words whitespace-normal">{attack.text}</p>
                      </div>
                    ))}
                    {!card.attacks && <p className="text-slate-800 text-[10px] font-black uppercase text-center py-12">No Offensive Moves.</p>}
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default CardDetailsModal;
